/**
 * Sysadmin Platform — Platform-Default Branding (Wave 1c W1c-11).
 *
 * Mounted at `/sysadmin/branding`. Edits the single-row
 * platform_branding table — these defaults are inherited by every tenant
 * that does NOT have its own organization_branding override.
 *
 * Endpoints:
 *   GET  /api/v1/sysadmin/branding
 *   PUT  /api/v1/sysadmin/branding
 *
 * v1 surfaces the textual + theme-color fields; full asset uploads at the
 * platform-default level are deferred to v1.1 (a dedicated platform-asset
 * table). For v1, asset slots accept asset IDs directly (rare — usually
 * left null so SYSTEM_DEFAULT logos render).
 */

import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Palette, Save, Undo2, Loader2, AlertTriangle } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  usePlatformBranding,
  useUpdatePlatformBranding,
  type PlatformBranding,
  type PlatformBrandingWrite,
} from '@/lib/use-platform-branding';
import { toast } from 'sonner';

export const Route = createFileRoute('/sysadmin/branding')({
  component: PlatformBrandingPage,
});

function PlatformBrandingPage() {
  const { data, isLoading, error } = usePlatformBranding();
  const update = useUpdatePlatformBranding();

  const [draft, setDraft] = useState<PlatformBrandingWrite>({});
  const [savedSnapshot, setSavedSnapshot] = useState<PlatformBranding | null>(null);

  useEffect(() => {
    if (data && !savedSnapshot) {
      setSavedSnapshot(data);
    }
  }, [data, savedSnapshot]);

  if (isLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-muted-foreground animate-pulse flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading platform branding…
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card>
          <CardContent className="py-10 text-center">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <h2 className="font-semibold">Failed to load platform branding</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {error?.message ?? 'Unknown error'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isDirty = Object.keys(draft).length > 0;

  const handleSave = async () => {
    try {
      const updated = await update.mutateAsync(draft);
      setSavedSnapshot(updated);
      setDraft({});
      toast.success('Platform branding saved');
    } catch (err) {
      toast.error('Failed to save platform branding', {
        description: (err as Error).message,
      });
    }
  };

  const handleReset = () => {
    setDraft({});
    toast.info('Reverted unsaved changes');
  };

  // Effective values for the form (draft > saved).
  const effective = {
    portalTitle: draft.portalTitle ?? data.portalTitle ?? '',
    displayMode: draft.displayMode ?? data.displayMode,
    footerText: draft.footerText ?? data.footerText ?? '',
    lightTheme: { ...data.lightTheme, ...(draft.lightTheme ?? {}) },
    darkTheme: { ...data.darkTheme, ...(draft.darkTheme ?? {}) },
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Palette className="h-6 w-6" />
            Platform-default branding
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            These defaults are inherited by every tenant that has not customised
            their own portal. Changes apply immediately to tenants without a
            local override.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={!isDirty || update.isPending}
            data-testid="platform-branding-reset"
          >
            <Undo2 className="h-4 w-4 mr-1" />
            Reset
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!isDirty || update.isPending}
            data-testid="platform-branding-save"
          >
            {update.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save changes
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Identity</CardTitle>
          <CardDescription>
            Portal title and footer text shown across every tenant that
            inherits the platform default.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="portalTitle">Portal title</Label>
            <Input
              id="portalTitle"
              value={effective.portalTitle}
              onChange={(e) =>
                setDraft((d) => ({ ...d, portalTitle: e.target.value || null }))
              }
              placeholder="e.g. ArgusAI"
              data-testid="platform-branding-portal-title"
            />
            <p className="text-xs text-muted-foreground">
              Falls back to <code>SYSTEM_DEFAULT_BRANDING.portalTitle</code> if
              unset.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="footerText">Footer text</Label>
            <Input
              id="footerText"
              value={effective.footerText}
              onChange={(e) =>
                setDraft((d) => ({ ...d, footerText: e.target.value || null }))
              }
              placeholder="e.g. © 2026 Argus Industrial AI"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayMode">Header display mode</Label>
            <select
              id="displayMode"
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              value={effective.displayMode}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  displayMode: e.target.value as 'full' | 'both' | 'icon-name',
                }))
              }
            >
              <option value="full">Full wordmark only</option>
              <option value="both">Icon + name</option>
              <option value="icon-name">Icon + portal title</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Theme colours</CardTitle>
          <CardDescription>
            Default light/dark palettes. Tenant overrides win unless the
            corresponding field is locked.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="light">
            <TabsList>
              <TabsTrigger value="light">Light theme</TabsTrigger>
              <TabsTrigger value="dark">Dark theme</TabsTrigger>
            </TabsList>
            <TabsContent value="light">
              <ColorPalette
                values={effective.lightTheme}
                onChange={(key, value) =>
                  setDraft((d) => ({
                    ...d,
                    lightTheme: { ...(d.lightTheme ?? {}), [key]: value },
                  }))
                }
              />
            </TabsContent>
            <TabsContent value="dark">
              <ColorPalette
                values={effective.darkTheme}
                onChange={(key, value) =>
                  setDraft((d) => ({
                    ...d,
                    darkTheme: { ...(d.darkTheme ?? {}), [key]: value },
                  }))
                }
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Separator />

      <div className="text-xs text-muted-foreground space-y-1">
        <div>
          Effective version: <code>{data.effectiveVersion}</code>
        </div>
        <div>
          Last updated:{' '}
          <code>{new Date(data.updatedAt).toLocaleString()}</code>
        </div>
        <div>
          Globally locked fields: {' '}
          {data.lockedFields.length === 0 ? (
            <span className="text-muted-foreground">none</span>
          ) : (
            <code>{data.lockedFields.join(', ')}</code>
          )}
        </div>
      </div>
    </div>
  );
}

const PALETTE_KEYS: Array<{ key: string; label: string }> = [
  { key: 'primary', label: 'Primary' },
  { key: 'primaryForeground', label: 'Primary foreground' },
  { key: 'background', label: 'Background' },
  { key: 'card', label: 'Card' },
  { key: 'text', label: 'Text' },
  { key: 'popover', label: 'Popover' },
  { key: 'sidebarBackground', label: 'Sidebar background' },
  { key: 'sidebarText', label: 'Sidebar text' },
  { key: 'sidebarActive', label: 'Sidebar active' },
  { key: 'sidebarActiveText', label: 'Sidebar active text' },
  { key: 'sidebarHover', label: 'Sidebar hover' },
];

function ColorPalette({
  values,
  onChange,
}: {
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
      {PALETTE_KEYS.map(({ key, label }) => {
        const value = values[key] ?? '';
        return (
          <div key={key} className="flex items-center gap-3">
            <div className="flex-1">
              <Label htmlFor={`palette-${key}`} className="text-xs">
                {label}
              </Label>
              <Input
                id={`palette-${key}`}
                type="text"
                value={value}
                onChange={(e) => onChange(key, e.target.value)}
                placeholder="#RRGGBB"
                className="font-mono text-xs h-8"
                pattern="^#[0-9a-fA-F]{6}$"
              />
            </div>
            <div
              className="h-8 w-8 rounded border"
              style={{
                backgroundColor: /^#[0-9a-fA-F]{6}$/.test(value) ? value : undefined,
              }}
              aria-hidden
            />
          </div>
        );
      })}
    </div>
  );
}
